import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';

import ShareModal from '../../Components/ShareModal';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { ShortsCard } from './ShortsCards';
import { fetchPosts } from '../../services/CommonFunctions';
import { handleFollow, handleLike, handleBookmark } from '../Post/PostCommonApi';

const Shorts: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { shorts: shortsData, currentPage, hasMore, loading } = useSelector((state: RootState) => state.short);
  const user = useSelector((state: RootState) => state.user.user);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<HTMLDivElement[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [selectedShortForShare, setSelectedShortForShare] = useState<any>(null);
  const [isMuted, setIsMuted] = useState<boolean>(true); // Global mute state for all videos

  const loadMoreShorts = useCallback(() => {
    if (hasMore && !loading) {
      fetchPosts(dispatch, 'video', currentPage + 1, 5, true);
    }
  }, [dispatch, currentPage, hasMore, loading]);

  useEffect(() => {
    fetchPosts(dispatch, 'video');
  }, []);

  // Handle scroll-to-short functionality when coming from shared link
  useEffect(() => {
    // Extract shortId from URL path (e.g., /shorts/123456789 from https://yourapp.com/shorts/123456789)
    const pathSegments = location.pathname.split('/');
    const isShortsRoute = pathSegments[1] === 'shorts';
    const shortId = isShortsRoute ? pathSegments[2] : pathSegments[pathSegments.length - 1];
    
    if (shortId && shortsData.length > 0) {
      // Find the short with the matching ID
      const targetShort = shortsData.find(short => short._id === shortId);
      if (targetShort) {
        // Find the index of the target short
        const targetIndex = shortsData.findIndex(short => short._id === shortId);
        if (targetIndex !== -1) {
          // Set the active index to the target short
          setActiveIndex(targetIndex);
          // Scroll to the short after a short delay to ensure it's rendered
          setTimeout(() => {
            const shortElement = document.querySelector(`[data-short-id="${shortId}"]`);
            if (shortElement) {
              shortElement.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start' 
              });
            }
          }, 500);
        }
      }
    }
  }, [location.pathname, shortsData]);

  const handleShare = (short: any) => {
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://ifinds.in' 
      : window.location.origin.replace('https://', 'http://');
    const shareUrl = `${baseUrl}/shorts/${short._id}`;
    const shareText = `Check out this short: ${short.description || short.title || 'Amazing video'}`;
    
    setSelectedShortForShare({
      ...short,
      shareUrl,
      shareText
    });
    setOpenShareModal(true);
  };

  const handleLikeClick = (id: string, isLiked: boolean) => {
    handleLike(id, dispatch, 'video');
  };

  const handleBookmarkClick = (id: string, isSaved: boolean) => {
    handleBookmark(id, dispatch, 'video');
  };

  const handleToggleMute = () => {
    setIsMuted((prev) => !prev);
  };

  useEffect(() => {
    const handleScroll = () => {
      // Calculate visibility ratio for each video element
      const visibilityRatios = videoRefs.current.map((ref) => {
        if (!ref) return 0;
        const rect = ref.getBoundingClientRect();
        const elementHeight = rect.height || 1; // fallback to prevent division by zero
        const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
        const ratio = visibleHeight / elementHeight;
        return ratio < 0 ? 0 : ratio; // ensure non-negative
      });

      // Find the element that is at least 60% visible and has the highest ratio
      let candidateIndex = activeIndex;
      let highestRatio = 0;

      visibilityRatios.forEach((ratio, idx) => {
        if (ratio >= 0.6 && ratio > highestRatio) {
          highestRatio = ratio;
          candidateIndex = idx;
        }
      });

      if (candidateIndex !== activeIndex) {
        setActiveIndex(candidateIndex);
        
        // Check if we're viewing the second-to-last item and need to load more
        if (candidateIndex >= shortsData.length - 2 && hasMore && !loading) {
          loadMoreShorts();
        }
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [activeIndex, loadMoreShorts, shortsData.length, hasMore, loading]);

  return (
    <>
      {
        shortsData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <Typography variant="h6">No shorts found</Typography>
          </Box>
        ) : (


          <Box
            ref={scrollContainerRef}
            sx={{
              height: 'calc(100vh - 56px)',
              overflowY: 'scroll',
              scrollSnapType: 'y mandatory',
              pb: { xs: '56px', sm: 0 },
            }}
          >
            {shortsData.map((item, index) => (
              <Box key={item._id} data-short-id={item._id}>
                <ShortsCard
                  item={item}
                  index={index}
                  videoRefs={videoRefs}
                  activeIndex={activeIndex}
                  handleFollow={() => handleFollow(item?.author?._id, item?._id, dispatch, 'video')}
                  handleShare={() => handleShare(item)}
                  isPreview={false}
                  currentUser={user}
                  onLike={handleLikeClick}
                  onBookmark={handleBookmarkClick}
                  isMuted={isMuted}
                  onToggleMute={handleToggleMute}
                />
              </Box>
            ))}
            
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, height: '100vh' }}>
                <CircularProgress />
              </Box>
            )}
            
            {!hasMore && shortsData.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, height: '100vh' }}>
                <Typography variant="body2" color="text.secondary">
                  No more shorts to load
                </Typography>
              </Box>
            )}

            <ShareModal
              open={openShareModal}
              onClose={() => {
                setOpenShareModal(false);
                setSelectedShortForShare(null);
              }}
              title={selectedShortForShare?.description || selectedShortForShare?.title || 'Short Video'}
              shareUrl={selectedShortForShare?.shareUrl || ''}
              shareText={selectedShortForShare?.shareText || ''}
            />
          </Box>
        )
      }
    </>
  );
};





export default Shorts;
